import {
  WidgetSettingsV1,
  WidgetSettingsV2,
  WidgetSettings,
  isV2,
  Page,
  WidgetLayout,
  PageV1,
  Group,
  ButtonV1,
  Button,
  Icon,
  ButtonTypeV1,
  BlockType,
  TBlock,
  OptionV1,
  Option,
  FormFieldV1,
  FormElement,
  FieldTypeV1,
  InputType,
  FormField,
  TeaserMode,
  Teaser,
  TeaserDevice,
} from '@gsc/types'

import classnames from 'classnames'

import { WidgetInstance } from './types'

const uuid = (): string => Math.random().toString(10).substring(2, 9)

const createBlock = <T extends BlockType>(type: T, props: Omit<TBlock<T>, 'type'>): TBlock<T> => {
  return {
    type,
    id: uuid(),
    ...props,
  } as unknown as TBlock<T>
}

const sendIcon = `
<svg viewbox="0 0 20 18">
  <path
    stroke="none"
    fill="currentColor"
    fill-rule="nonzero"
    d="M20 1l-6 14.8c-.1.4-.5.7-.9.8-.4.1-.9 0-1.2-.2l-5.6-3.6L19 1.3 4.6 11.6.6 9C0 8.7 0 8.3 0 7.9c0-.4.4-.8.8-1L19.4.6l.5.1c0 .1.1.3 0 .4zM6 17.4a1 1 0 0 1-.8 0 1 1 0 0 1-.6-.7l-.7-4v-.2l4.6 3.3-2.4 1.6z"
  />
</svg>
`

const closeIcon = `
<svg viewbox="0 0 24 24">
  <g fill="none" fill-rule="evenodd">
    <path d="M1.458 1.458l21.084 21.084m0-21.084L1.458 22.542" />
  </g>
</svg>
`

export function buttonsToV2(buttons: ButtonV1[]): Group {
  const children = buttons
    .filter(({ visible = true }) => visible)
    .map(({ type, label, actions }): Button => {
      const icon: Icon = label
        ? null
        : createBlock(BlockType.Icon, {
            src: type === ButtonTypeV1.Secondary ? closeIcon : sendIcon,
          })

      return createBlock(BlockType.Button, {
        className: [type, ...(icon ? ['icon'] : [])].join(' '),
        label,
        actions,
        icon,
      })
    })

  return children.length > 0 ? createBlock(BlockType.Group, { className: 'buttons', children }) : null
}

function optionsToOptions(options: OptionV1[]): Option[] {
  return options.map(
    ({ id, name: value, label, defaultChecked }): Option => ({ id: id || uuid(), value, label, defaultChecked }),
  )
}

function fieldToFormElement(field: FormFieldV1): FormElement {
  const {
    type,
    name,
    label,
    placeholder,
    required,
    options = [],
    randomizeOptions: randomizeOrder,
    defaultValue,
  } = field

  switch (type) {
    case FieldTypeV1.Text:
    case FieldTypeV1.Email:
    case FieldTypeV1.Tel:
      return createBlock(BlockType.Input, {
        inputType: type as unknown as InputType,
        name,
        placeholder,
        required,
        defaultValue,
      })

    case FieldTypeV1.Textarea:
      return createBlock(BlockType.Textarea, {
        name,
        placeholder,
        required,
        defaultValue,
      })

    case FieldTypeV1.Select:
      return createBlock(BlockType.Select, {
        name,
        placeholder,
        required,
        options: optionsToOptions(options),
        randomizeOrder,
      })

    case FieldTypeV1.Checkbox:
      return createBlock(BlockType.CheckboxGroup, {
        name,
        required,
        options: optionsToOptions(options),
        randomizeOrder,
      })

    case FieldTypeV1.Radio:
      return createBlock(BlockType.RadioGroup, {
        name,
        required,
        options: optionsToOptions(options),
        randomizeOrder,
      })

    case FieldTypeV1.Flag:
      return createBlock(BlockType.Flag, {
        name,
        label,
        required,
        value: name,
        defaultChecked: Boolean(defaultValue),
      })

    case FieldTypeV1.Rating:
      return createBlock(BlockType.Rating, {
        name,
        required,
      })

    case FieldTypeV1.Hidden:
      return createBlock(BlockType.Hidden, {
        name,
        defaultValue,
      })
  }
}

function fieldsToV2(fields: FormFieldV1[]): Group {
  const children = fields.map((f: FormFieldV1): FormField => {
    const label = f.type !== FieldTypeV1.Flag ? f.label : ''
    return createBlock(BlockType.FormField, {
      label: createBlock(BlockType.Text, { text: label, tag: 'label' }),
      className: f.type === FieldTypeV1.Hidden ? 'hidden' : '',
      element: fieldToFormElement(f),
    })
  })

  return children.length > 0 ? createBlock(BlockType.Group, { className: 'fields', children }) : null
}

function pageToV2(page: PageV1, { layout }: WidgetSettingsV1): Page {
  const {
    name,
    label,
    style,
    data: { title, description, note, buttons, fields, backgroundActions: actions },
  } = page

  const fieldsGroup = fieldsToV2(fields)
  const buttonsGroup = buttonsToV2(buttons)

  const contentGroup = createBlock(BlockType.Group, {
    className: classnames('content', !fieldsGroup && 'no-fields'),
    children: [
      layout !== WidgetLayout.Panel && title
        ? createBlock(BlockType.Text, { text: title, tag: 'h1', className: 'title' })
        : null,

      description ? createBlock(BlockType.Text, { text: description, className: 'description' }) : null,

      createBlock(BlockType.Group, {
        className: 'form',
        children: [fieldsGroup, buttonsGroup].filter(Boolean),
      }),

      note ? createBlock(BlockType.Text, { text: note, role: 'note', className: 'note' }) : null,
    ].filter(Boolean),
  })

  const bodyGroup = createBlock(BlockType.Group, { className: 'body', children: [contentGroup] })

  return {
    id: name,
    label,
    style,
    root: createBlock(BlockType.Group, { actions, children: [bodyGroup] }),
  }
}

function migrateCss(css = ''): string {
  return css.replace(/\.form\.submit/g, '.submit').replace(/\.root\.form-sending\s+\.content/g, '.form-sending')
}

function migrateToV2(settings: WidgetSettingsV1): WidgetSettingsV2 {
  const { pages: oldPages, layout, style, ...rest } = settings
  const pages: Page[] = oldPages.map(p => pageToV2(p, settings))
  const data: WidgetSettingsV2['data'] =
    layout === WidgetLayout.Panel
      ? {
          header: {
            text: oldPages[0]?.data?.title || '',
            tag: 'h1',
          },
        }
      : {}

  return {
    ...rest,
    style: {
      ...style,
      css: migrateCss(style.css),
    },
    layout,
    version: 'V1', // use V1 for widgets migrated on the fly
    pages,
    data,
  }
}

type WidgetEnv = {
  device: string | null
}

function shouldShowTeaserForEnv(teaser: Teaser, env: WidgetEnv): boolean {
  if (!teaser) {
    return false
  }

  const { device = TeaserDevice.All } = teaser
  const target = !env.device
    ? TeaserDevice.All
    : env.device.toLowerCase() === 'desktop'
      ? TeaserDevice.Desktop
      : TeaserDevice.Mobile

  return device === target || [target, device].includes(TeaserDevice.All)
}

export function createWidgetInstance(ws: WidgetSettings, env: WidgetEnv): WidgetInstance {
  const settings = isV2(ws) ? ws : migrateToV2(ws)
  const { preview = {}, pages, data, teaser: originalTeaser, layout } = settings
  const { page = 0, pageStack = [], view } = preview

  const teaser = layout !== WidgetLayout.Inline && shouldShowTeaserForEnv(originalTeaser, env) ? originalTeaser : null

  const showTeaser = !!teaser && (view ? view === 'teaser' : [TeaserMode.Front, TeaserMode.Both].includes(teaser.mode))

  return {
    ...settings,
    teaser,
    data,
    pages,
    pageStack,
    show: showTeaser ? 'teaser' : 'main',
    activePage: Math.max(0, Math.min(page, pages.length - 1)),
    formState: { data: {}, valid: true },
  }
}
